/**
 * grass_blades.ts — the VERTEX-PROCEDURAL blade and the clump card (2026-09-25, visual-realism R2).
 *
 * No per-instance buffers and no per-instance CPU work: each ring is ONE plain Mesh over an
 * InstancedBufferGeometry whose only attribute is the blade template (x = side −1/0/+1, y = height
 * fraction). The vertex shader derives everything else from `instanceIndex`:
 *
 *   instance i → lattice cell (base + (i mod G, ⌊i / G⌋))           world-locked, camera-snapped
 *   cell → PCG hash of the cell's ABSOLUTE index (mod 65,536)          jitter, yaw, height, keep
 *   position → the field texture (grass_field.ts)                      ground height (bilinear),
 *                                                                      density, blade height, colour
 *   position → the 4 m clump cell (nearest of the 2×2 jittered centres) clump height / hue / lean
 *   + wind (lib/wind.ts windVector) as a travelling gust + flutter, + the trample field (lib/foliage_trample)
 *   a blade whose hash exceeds density × ring weight COLLAPSES to a point (zero-area — rasterises nothing).
 *
 * SIGN SAFETY: three's `hash()` casts a FLOAT to uint and saturates negatives to 0 (the −X/−Z half of
 * the world would hash alike). The cells here are wrapped into 0..65,535 with a floored mod BEFORE
 * the uint cast, and the two 16-bit halves are combined in uint arithmetic — exact on both backends.
 *
 * MOTION VECTORS: the positionNode assigns `positionPrevious` = the same blade with the wind one
 * frame ago (windPrevDt) — the positionPrevious LAW; the lattice re-snap moves no blade (a blade is
 * a cell, not a slot), so it contributes no velocity.
 *
 * The CPU mirrors (`pcg32`, `bladeAt`) are what verify:grass pins: the same inputs give the same
 * blade, a rebase gives the same blade, and the two halves of the world hash apart.
 */
import * as THREE from 'three/webgpu';
import {
  Fn, float, vec2, vec3, vec4, ivec2, uniform, textureLoad, texture as tslTexture, instanceIndex, positionGeometry,
  positionPrevious, positionWorld, cameraPosition, cameraViewMatrix, normalize, mix, floor, fract, sin, cos, min, max,
  clamp, smoothstep, select, length, dot, pow, abs, step, varyingProperty, fwidth,
} from 'three/tsl';
import type { GrassRing } from './grass_params.js';
import { GRASS_FIELD_N, GRASS_TEXEL_M, GRASS_FIELD_REACH_M } from './grass_field.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

/** The hash lattice wraps every 65,536 cells (10.9 km at 1/6 m — never seen twice). */
export const GRASS_HASH_WRAP = 65536;
/** Clump cells (m) — whole metres so the anchor's offset in clump cells is an integer. */
export const GRASS_CLUMP_M = 4;

// ── CPU mirrors ─────────────────────────────────────────────────────────────
/** PCG (pcg-random.org via three's hash()): uint32 → uint32. */
export function pcg32(v: number): number {
  const state = (Math.imul(v >>> 0, 747796405) + 2891336453) >>> 0;
  const word = Math.imul(((state >>> ((state >>> 28) + 4)) ^ state) >>> 0, 277803737) >>> 0;
  return ((word >>> 22) ^ word) >>> 0;
}
const wrap = (a: number, n: number): number => ((a % n) + n) % n;
/** The hash of an ABSOLUTE lattice cell (sign-safe: wrapped to 0..65,535 per axis first). */
export function cellHash(ax: number, az: number, salt = 0): number {
  const hx = wrap(ax, GRASS_HASH_WRAP), hz = wrap(az, GRASS_HASH_WRAP);
  return pcg32(((hx * 65536 + hz) >>> 0) ^ salt);
}
const u16 = (h: number, hi: boolean): number => (hi ? (h >>> 16) : (h & 0xffff)) / 65536;

export interface Blade { cellX: number; cellZ: number; x: number; z: number; yaw: number; hScale: number; keepR: number; r5: number }
/**
 * The blade instance `i` of `ring` stands on, given the anchor (whole metres) and the camera
 * (render space = absolute − anchor). Mirrors the shader exactly (positions are ABSOLUTE here).
 */
export function bladeAt(i: number, ring: Pick<GrassRing, 'grid' | 'cellsPerM'>, anchorX: number, anchorZ: number, camLocalX: number, camLocalZ: number): Blade {
  const [bx, bz] = ringBase(ring, camLocalX, camLocalZ);
  const iz = Math.floor(i / ring.grid), ix = i - iz * ring.grid;
  const cx = bx + ix, cz = bz + iz; // local cells
  const ax = cx + anchorX * ring.cellsPerM, az = cz + anchorZ * ring.cellsPerM; // absolute cells (integers: the lattice law)
  const h0 = cellHash(ax, az), h1 = pcg32(h0), h2 = pcg32(h1);
  const r0 = u16(h0, false), r1 = u16(h0, true);
  return {
    cellX: ax, cellZ: az,
    x: (cx + 0.1 + 0.8 * r0) / ring.cellsPerM + anchorX,
    z: (cz + 0.1 + 0.8 * r1) / ring.cellsPerM + anchorZ,
    yaw: u16(h1, false) * Math.PI * 2, hScale: 0.7 + 0.55 * u16(h1, true), keepR: u16(h2, false), r5: u16(h2, true),
  };
}
/** The lattice corner cell (local) of a ring for a camera at render-space (x, z): ⌊cam·k⌋ − G/2. */
export function ringBase(ring: Pick<GrassRing, 'grid' | 'cellsPerM'>, camLocalX: number, camLocalZ: number): [number, number] {
  return [Math.floor(camLocalX * ring.cellsPerM) - ring.grid / 2, Math.floor(camLocalZ * ring.cellsPerM) - ring.grid / 2];
}
/** The anchor's offset in hash cells (absolute cell = local cell + this, mod the wrap). */
export function ringHashOffset(ring: Pick<GrassRing, 'cellsPerM'>, anchorX: number, anchorZ: number): [number, number] {
  return [wrap(Math.round(anchorX * ring.cellsPerM), GRASS_HASH_WRAP), wrap(Math.round(anchorZ * ring.cellsPerM), GRASS_HASH_WRAP)];
}
/** The clump uniform: (anchor mod 4 per axis, the rest in clump cells mod the wrap). */
export function clumpOffset(anchorX: number, anchorZ: number): [number, number, number, number] {
  const ax = wrap(Math.round(anchorX), GRASS_CLUMP_M), az = wrap(Math.round(anchorZ), GRASS_CLUMP_M);
  return [ax, az, wrap((Math.round(anchorX) - ax) / GRASS_CLUMP_M, GRASS_HASH_WRAP), wrap((Math.round(anchorZ) - az) / GRASS_CLUMP_M, GRASS_HASH_WRAP)];
}

// ── templates ───────────────────────────────────────────────────────────────
/** One blade: `segments` quads up a strip + a tip triangle. position = (side, t, 0). ONE attribute (the 8-buffer law). */
export function bladeTemplate(segments: 2 | 4, instances: number): THREE.InstancedBufferGeometry {
  const pos: number[] = []; const idx: number[] = [];
  for (let k = 0; k < segments; k++) { const t = k / segments; pos.push(-1, t, 0, 1, t, 0); }
  pos.push(0, 1, 0);
  for (let k = 0; k < segments - 1; k++) { const a = 2 * k; idx.push(a, a + 1, a + 3, a, a + 3, a + 2); }
  const top = 2 * (segments - 1); idx.push(top, top + 1, 2 * segments);
  const g = new THREE.InstancedBufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.instanceCount = instances;
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6); // frustumCulled is off anyway — the layer is around the camera
  return g;
}
/** One camera-yawed card: a quad, position = (x ∈ ±0.5, y ∈ 0..1, 0). */
export function cardTemplate(instances: number): THREE.InstancedBufferGeometry {
  const g = new THREE.InstancedBufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute([-0.5, 0, 0, 0.5, 0, 0, 0.5, 1, 0, -0.5, 1, 0], 3));
  g.setIndex([0, 1, 2, 0, 2, 3]);
  g.instanceCount = instances;
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
  return g;
}

// ── shared uniforms ─────────────────────────────────────────────────────────
export interface GrassShared {
  field: THREE.DataTexture;
  /** (2·anchor) mod 256 — the field's slot offset. */
  fieldOff: N;
  /** Camera, render space. */
  cam: N;
  /** (anchor mod 4 x, z, clump cell offset x, z). */
  clump: N;
  /** Master density (the menu / probe lever). */
  density: N;
  /** Wind (lib/wind.ts windVector node) and last frame's dt. */
  wind: N;
  windDt: N;
  /** Trample field + bounds (render space), or null. */
  trample: THREE.Texture | null;
  trampleBounds: N;
  /** Sun direction (world, toward the sun) and night factor (SkyModule). */
  sunDir: N;
  night: N;
  /** Seasonal / dryness tint multiplier on the turf (R9). */
  tint: N;
  /** A floor under the wind strength (probe lever: sway visibly on a calm day). */
  windMin: N;
}
export interface RingUniforms { base: N; hashOff: N }

const HASH_WRAP = float(GRASS_HASH_WRAP);
/** TSL PCG on a uint node. */
const pcgU = (v: N): N => {
  const state = v.mul(747796405).add(2891336453);
  const word = state.shiftRight(state.shiftRight(28).add(4)).bitXor(state).mul(277803737);
  return word.shiftRight(22).bitXor(word);
};
/** TSL cell hash: wrap each axis into 0..65,535 (floored mod), combine in uint. */
const cellHashU = (c: N): N => {
  const w = c.sub(HASH_WRAP.mul(floor(c.div(HASH_WRAP))));
  return pcgU(w.x.toUint().mul(65536).add(w.y.toUint()));
};
const lo16 = (h: N): N => h.bitAnd(0xffff).toFloat().div(65536);
const hi16 = (h: N): N => h.shiftRight(16).toFloat().div(65536);
const srgbToLin = (c: N): N => pow(c, vec3(2.2));

/** Everything a ring's vertex stage derives about its blade: the ground, the rules, the clump. */
function deriveBlade(ring: GrassRing, sh: GrassShared, ru: RingUniforms): N {
  const G = float(ring.grid), K = float(ring.cellsPerM);
  const i = instanceIndex.toFloat();
  const iz = floor(i.div(G)); const ix = i.sub(iz.mul(G));
  const cell = ru.base.add(vec2(ix, iz));
  const h0 = cellHashU(cell.add(ru.hashOff)); const h1 = pcgU(h0); const h2 = pcgU(h1);
  const r0 = lo16(h0), r1 = hi16(h0), r2 = lo16(h1), r3 = hi16(h1), r4 = lo16(h2), r5 = hi16(h2);
  const pos = cell.add(0.1).add(vec2(r0, r1).mul(0.8)).div(K).toVar('gPos'); // render-space XZ
  const d = length(pos.sub(sh.cam.xz));
  const inW = ring.rIn <= 0 ? float(1) : smoothstep(float(ring.rIn - ring.fadeIn), float(ring.rIn), d);
  const outW = float(1).sub(smoothstep(float(ring.rOut - ring.fadeOut), float(ring.rOut), d));
  // the field: 4 taps around the texel-centre lattice, slots wrap mod N
  const f = pos.div(GRASS_TEXEL_M).sub(0.5);
  const t0 = floor(f); const a = f.sub(t0);
  const NN = float(GRASS_FIELD_N);
  const slot = (o: N): N => { const s = t0.add(o).add(sh.fieldOff); return s.sub(NN.mul(floor(s.div(NN)))); };
  const s00 = slot(vec2(0, 0)), s10 = slot(vec2(1, 0)), s01 = slot(vec2(0, 1)), s11 = slot(vec2(1, 1));
  const q00 = textureLoad(sh.field, ivec2(s00)), q10 = textureLoad(sh.field, ivec2(s10)), q01 = textureLoad(sh.field, ivec2(s01)), q11 = textureLoad(sh.field, ivec2(s11));
  const bl = (c: (q: N) => N): N => mix(mix(c(q00), c(q10), a.x), mix(c(q01), c(q11), a.x), a.y);
  const ground = bl((q) => q.x);
  const dens = bl((q) => q.y);
  const bladeH = bl((q) => q.z);
  const valid = min(min(q00.y, q10.y), min(q01.y, q11.y)).greaterThanEqual(0).and(d.lessThan(GRASS_FIELD_REACH_M));
  const unpack = (p: N): N => { const r = floor(p.div(65536)); const rest = p.sub(r.mul(65536)); const g = floor(rest.div(256)); return srgbToLin(vec3(r, g, rest.sub(g.mul(256))).div(255)); };
  const turf = bl((q) => unpack(q.w)).mul(sh.tint);
  // the clump: the nearest of the 2×2 jittered centres of the 4 m lattice (world-locked through `clump`)
  const pa = pos.add(sh.clump.xy); const cc = pa.div(GRASS_CLUMP_M);
  const n0 = floor(cc.sub(0.5));
  const best = vec4(1e9, 0, 0, 0).toVar('gClumpBest'); // (dist², centre x, centre z, id hash)
  for (let dz = 0; dz <= 1; dz++) for (let dx = 0; dx <= 1; dx++) {
    const c = n0.add(vec2(dx, dz));
    const hc = cellHashU(c.add(sh.clump.zw));
    const ctr = c.add(0.2).add(vec2(lo16(hc), hi16(hc)).mul(0.6)).mul(GRASS_CLUMP_M).sub(sh.clump.xy);
    const dd = dot(pos.sub(ctr), pos.sub(ctr));
    const better = dd.lessThan(best.x);
    best.assign(select(better, vec4(dd, ctr.x, ctr.y, pcgU(hc).toFloat().div(4294967296)), best));
  }
  const clumpId = best.w;
  const clumpH = float(0.68).add(fract(clumpId.mul(7.31)).mul(0.62));
  const clumpHue = fract(clumpId.mul(13.7)).mul(2).sub(1);
  const away = pos.sub(best.yz);
  const awayLen = length(away).max(1e-3);
  const lean = away.div(awayLen).mul(float(0.12).add(float(0.16).mul(smoothstep(float(0), float(2.6), awayLen))));
  const keep = valid.and(r4.lessThan(dens.mul(inW).mul(outW).mul(ring.keep).mul(sh.density)));
  const hScale = float(0.7).add(r3.mul(0.55));
  return { pos, ground, bladeH, turf, keep, r2, r3, r5, hScale, clumpH, clumpHue, lean, phase: clumpId };
}

/** Wind at a blade: the horizontal tip offset (m per m of blade height) at time `t`. */
function windTip(pos: N, sh: GrassShared, phase: N, r5: N, t: N): N {
  const dir = vec2(sh.wind.x, sh.wind.y);
  const s = float(0.12).add(clamp(max(sh.wind.z, sh.windMin), 0, 1.5));
  const gust = s.mul(float(0.5).add(sin(dot(pos, dir).mul(0.28).sub(t.mul(1.9)).add(phase.mul(6.28))).mul(0.5)));
  const flutter = sin(t.mul(6.5).add(r5.mul(40))).mul(0.05).mul(float(0.4).add(s));
  const perp = vec2(dir.y.negate(), dir.x);
  return dir.mul(gust.mul(0.42)).add(perp.mul(flutter));
}
/** The trample bend (m) at a render-space position, 0 outside the field's region. */
function trampleAt(pos: N, sh: GrassShared): N {
  if (!sh.trample) return vec2(0, 0);
  const uvT = pos.sub(sh.trampleBounds.xy).div(sh.trampleBounds.zw);
  const inside = step(0, uvT.x).mul(step(uvT.x, 1)).mul(step(0, uvT.y)).mul(step(uvT.y, 1));
  const tex = tslTexture(sh.trample) as unknown as { sample(uv: N): N };
  return tex.sample(uvT).xy.mul(inside);
}

function applySurface(m: THREE.MeshStandardNodeMaterial, sh: GrassShared, vColor: N, vN: N, vT: N): void {
  m.colorNode = vColor;
  m.normalNode = normalize(cameraViewMatrix.mul(vec4(vN, 0)).xyz);
  m.roughnessNode = mix(float(0.92), float(0.72), vT);
  m.metalnessNode = float(0);
  // Light THROUGH the blade when it stands between the eye and a low sun (tips most).
  const V = normalize(positionWorld.sub(cameraPosition));
  const back = pow(max(dot(V, sh.sunDir), 0), 5).mul(smoothstep(float(-0.05), float(0.12), sh.sunDir.y)).mul(float(1).sub(sh.night));
  m.emissiveNode = vColor.mul(back.mul(vT).mul(0.55)).mul(vec3(1.0, 0.9, 0.62));
  m.side = THREE.DoubleSide;
}

/** The blade ring's material (MeshStandardNodeMaterial, opaque, double-sided). */
export function createBladeMaterial(ring: GrassRing, sh: GrassShared, ru: RingUniforms): THREE.MeshStandardNodeMaterial {
  const m = new THREE.MeshStandardNodeMaterial();
  m.name = `grass-blades-${ring.segments}`;
  const vColor = varyingProperty('vec3', 'vGrassColor');
  const vN = varyingProperty('vec3', 'vGrassNormal');
  const vT = varyingProperty('float', 'vGrassT');
  m.positionNode = Fn(() => {
    const b = deriveBlade(ring, sh, ru);
    const side = positionGeometry.x, t = positionGeometry.y;
    const H = b.bladeH.mul(b.hScale).mul(b.clumpH).max(0.02).toVar('gH');
    const yaw = b.r2.mul(6.2831853);
    const wdir = vec3(sin(yaw).negate(), 0, cos(yaw)); // the blade's width axis
    const facing = vec2(cos(yaw), sin(yaw));
    const width = float(ring.width).mul(float(0.8).add(b.r5.mul(0.4))).mul(pow(float(1).sub(t), 0.75));
    const bend = trampleAt(b.pos, sh).mul(1.25);
    const shape = (tNow: N): N => {
      const tip = b.lean.add(windTip(b.pos, sh, b.phase, b.r5, tNow)).mul(H).add(bend);
      const tl = length(tip); const tipC = tip.mul(min(float(1), H.mul(0.9).div(tl.max(1e-4))));
      const disp = tipC.mul(t.mul(t));
      const y = H.mul(t).sub(dot(disp, disp).mul(0.5).div(H));
      return vec3(b.pos.x, b.ground.sub(0.05), b.pos.y).add(wdir.mul(side.mul(width).mul(0.5))).add(vec3(disp.x, y, disp.y));
    };
    const k = select(b.keep, float(1), float(0));
    const collapse = (p: N): N => mix(vec3(b.pos.x, b.ground.sub(0.5), b.pos.y), p, k);
    positionPrevious.assign(collapse(shape(sh.wind.w.sub(sh.windDt))));
    const tipNow = b.lean.add(windTip(b.pos, sh, b.phase, b.r5, sh.wind.w));
    vN.assign(normalize(vec3(facing.x.mul(0.22).add(tipNow.x.mul(0.6)), 1, facing.y.mul(0.22).add(tipNow.y.mul(0.6)))));
    vT.assign(t);
    // colour: dark at the root (the turf's own shade), the turf mid-blade, lighter and warmer at the tip
    const hue = vec3(1).add(vec3(0.07, 0.02, -0.07).mul(b.clumpHue));
    const value = float(0.86).add(b.r3.mul(0.28));
    const root = b.turf.mul(0.42), tipC = b.turf.mul(vec3(1.16, 1.12, 0.86)).add(vec3(0.012, 0.01, 0));
    const c = mix(mix(root, b.turf, smoothstep(float(0), float(0.45), t)), tipC, smoothstep(float(0.55), float(1), t));
    vColor.assign(c.mul(hue).mul(value));
    return collapse(shape(sh.wind.w));
  })();
  applySurface(m, sh, vColor, vN, vT);
  return m;
}

/** The clump-card ring's material: a camera-yawed quad of procedural blades (alpha-tested, no texture). */
export function createCardMaterial(ring: GrassRing, sh: GrassShared, ru: RingUniforms): THREE.MeshStandardNodeMaterial {
  const m = new THREE.MeshStandardNodeMaterial();
  m.name = 'grass-cards';
  const vColor = varyingProperty('vec3', 'vGrassColor');
  const vN = varyingProperty('vec3', 'vGrassNormal');
  const vT = varyingProperty('float', 'vGrassT');
  const vUv = varyingProperty('vec2', 'vGrassUv');
  const vSeed = varyingProperty('float', 'vGrassSeed');
  m.positionNode = Fn(() => {
    const b = deriveBlade(ring, sh, ru);
    const sx = positionGeometry.x, t = positionGeometry.y;
    const H = b.bladeH.mul(float(0.85).add(b.r3.mul(0.4))).mul(b.clumpH).mul(1.15).max(0.05).toVar('gH');
    const toCam = sh.cam.xz.sub(b.pos); const tc = toCam.div(length(toCam).max(1e-3));
    const right = vec3(tc.y.negate(), 0, tc.x);
    const W = H.mul(ring.width);
    const shape = (tNow: N): N => {
      const tip = b.lean.add(windTip(b.pos, sh, b.phase, b.r5, tNow)).mul(H);
      return vec3(b.pos.x, b.ground.sub(0.05), b.pos.y).add(right.mul(sx.mul(W))).add(vec3(tip.x.mul(t).mul(t), H.mul(t), tip.y.mul(t).mul(t)));
    };
    const k = select(b.keep, float(1), float(0));
    const collapse = (p: N): N => mix(vec3(b.pos.x, b.ground.sub(0.5), b.pos.y), p, k);
    positionPrevious.assign(collapse(shape(sh.wind.w.sub(sh.windDt))));
    vN.assign(normalize(vec3(tc.x.mul(0.3), 1, tc.y.mul(0.3))));
    vT.assign(t);
    vUv.assign(vec2(sx.add(0.5), t));
    vSeed.assign(b.r5);
    const hue = vec3(1).add(vec3(0.07, 0.02, -0.07).mul(b.clumpHue));
    vColor.assign(b.turf.mul(hue).mul(float(0.84).add(b.r3.mul(0.26))));
    return collapse(shape(sh.wind.w));
  })();
  // procedural blades on the card: 6 tapered, leaning stems; alpha = any stem covers the texel
  const cover = Fn(() => {
    const uvC = vUv, seed = vSeed;
    const acc = float(0).toVar('gCover');
    for (let k = 0; k < 6; k++) {
      const kk = float(k + 1);
      const cx = fract(seed.mul(kk.mul(7.13)).add(k * 0.173)).mul(0.8).add(0.1);
      const hk = fract(seed.mul(kk.mul(13.7)).add(k * 0.31)).mul(0.45).add(0.55);
      const lk = fract(seed.mul(kk.mul(5.3)).add(k * 0.47)).sub(0.5).mul(0.55);
      const x = cx.add(lk.mul(uvC.y.mul(uvC.y)));
      const hw = float(0.075).mul(float(1).sub(uvC.y.div(hk))).max(0);
      const dist = abs(uvC.x.sub(x)).sub(hw);
      const aa = fwidth(uvC.x).max(1e-4);
      acc.assign(max(acc, float(1).sub(smoothstep(aa.negate(), aa, dist)).mul(step(uvC.y, hk))));
    }
    return acc;
  })();
  const root = vColor.mul(0.45), tipC = vColor.mul(vec3(1.16, 1.12, 0.86));
  applySurface(m, sh, mix(mix(root, vColor, smoothstep(float(0), float(0.4), vT)), tipC, smoothstep(float(0.55), float(1), vT)), vN, vT);
  m.opacityNode = cover;
  m.alphaTest = 0.5;
  return m;
}

/** Fresh per-ring uniforms. */
export function ringUniforms(): RingUniforms { return { base: uniform(new THREE.Vector2()), hashOff: uniform(new THREE.Vector2()) }; }
